import React, { useLayoutEffect, useRef, useState } from "react";
import {
   CakeSlice,
   ChevronDown,
   ExternalLink,
   Gift,
   Info,
   Minus,
   Phone,
   Plus,
   Salad,
   ShoppingBag,
   Store,
   UtensilsCrossed,
} from "lucide-react";

import logoLight from "../../../assets/shared/dishonline-logo-white-sm.webp";
import logoDark from "../../../assets/shared/dishonline-logo-inverse-sm.webp";
import banner from "../../../assets/admin/landing/kitchen-sm.webp";
import { readableTextOn } from "../../../themes";
import { CATEGORIES, DISHES, SHOP, dish } from "./data";
import { euro } from "./primitives";

/*
 * Verkleinde kopie van de echte webshop (components/customer: Shop, ItemCard, ShopInfo,
 * Cart, NavCustomer) met dezelfde maten, zodat de preview op de landingspagina er echt
 * uitziet als de shop. Thema-tokens komen uit tailwind.config.js ([data-ui="apple"]) en
 * src/themes/classic.css. Wijzigt de shop, pas dan ook dit aan.
 */

const SYSTEM = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", "Geist", sans-serif';
const CAT_ICONS = { voor: Salad, hoofd: UtensilsCrossed, dessert: CakeSlice };

export function shopTokens(theme, dark, brand = "#1d1d1f") {
   if (theme === "classic") {
      const t = dark
         ? {
              page: "#14120f",
              panel: "#14120f",
              tertiary: `color-mix(in srgb, ${brand} 8%, #1d1a16)`,
              text: "#f4efe6",
              secondary: "rgba(244,239,230,0.68)",
              hair: "rgba(244,239,230,0.12)",
              line: `color-mix(in srgb, ${brand} 55%, transparent)`,
              mediaLine: "rgba(255,255,255,0.75)",
           }
         : {
              page: "#fbf8f2",
              panel: "#fbf8f2",
              tertiary: `color-mix(in srgb, ${brand} 4%, #f3eee5)`,
              text: "#1c1a17",
              secondary: "#6b645c",
              hair: "rgba(28,26,23,0.12)",
              line: `color-mix(in srgb, ${brand} 32%, transparent)`,
              mediaLine: "rgba(255,255,255,0.75)",
           };
      return {
         ...t,
         classic: true,
         dark,
         brand,
         accent: brand,
         accentText: readableTextOn(brand),
         ring: "none",
         r: { panel: 0, card: 0, inner: 0, control: 0, thumb: 0, pill: 0 },
         sans: '"Jost", sans-serif',
         display: '"Cormorant Garamond", Georgia, serif',
      };
   }
   const t = dark
      ? {
           page: "#000000",
           panel: "#1c1c1e",
           tertiary: "#2c2c2e",
           text: "#f5f5f7",
           secondary: "rgba(235,235,245,0.68)",
           hair: "rgba(255,255,255,0.08)",
           line: "#38383a",
           accent: "#f5f5f7",
           accentText: "#000000",
           ring: "0 0 0 1px rgba(255,255,255,0.06)",
        }
      : {
           page: "#f5f5f7",
           panel: "#ffffff",
           tertiary: "#f2f2f7",
           text: "#1d1d1f",
           secondary: "#6e6e73",
           hair: "rgba(0,0,0,0.06)",
           line: "rgb(235,235,235)",
           accent: "#1d1d1f",
           accentText: "#ffffff",
           ring: "0 0 0 1px rgba(0,0,0,0.04)",
        };
   return {
      ...t,
      classic: false,
      dark,
      r: { panel: 24, card: 20, inner: 16, control: 14, thumb: 12, pill: 999 },
      sans: SYSTEM,
      display: SYSTEM,
   };
}

/* ---------------- bouwstenen ---------------- */

// Vlak: afgerond met een haarlijn (modern) of het dubbele kader in de merkkleur (klassiek)
const frame = (t) =>
   t.classic
      ? { background: t.panel, border: `2px solid ${t.brand}`, outline: `1px solid ${t.brand}`, outlineOffset: -7 }
      : { background: t.panel, borderRadius: t.r.panel, boxShadow: t.ring };

const heading = (t, size) =>
   t.classic
      ? { fontFamily: t.display, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.14em", lineHeight: 1.15, fontSize: size }
      : { fontWeight: 700, letterSpacing: "-0.022em", fontSize: size };

const label = (t) =>
   t.classic ? { fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.14em" } : { fontSize: 14 };

function PlusButton({ t }) {
   return t.classic ? (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center" style={{ border: `1px solid ${t.brand}`, color: t.brand }}>
         <Plus size={15} strokeWidth={2} />
      </span>
   ) : (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center" style={{ borderRadius: 999, background: t.accent, color: t.accentText }}>
         <Plus size={18} strokeWidth={2.75} />
      </span>
   );
}

function Media({ t, d, className, style }) {
   return (
      <div className={`relative overflow-hidden ${className}`} style={{ background: t.tertiary, ...style }}>
         <img src={d.img} alt="" loading="lazy" decoding="async" className="!h-full !w-full object-cover" />
         {t.classic && <span className="pointer-events-none absolute inset-[6px]" style={{ border: `1px solid ${t.mediaLine}` }} />}
      </div>
   );
}

/* Gerecht als kaart (desktop) of als rij met foto rechts (telefoon), zoals ItemCard */
function ItemCard({ t, d, row = false }) {
   const title = t.classic
      ? { fontFamily: t.display, fontWeight: 600, fontSize: 20, lineHeight: 1.15 }
      : { fontWeight: 600, fontSize: 17, letterSpacing: "-0.01em", lineHeight: 1.3 };
   const desc = t.classic
      ? { fontFamily: t.display, fontStyle: "italic", fontSize: 17, lineHeight: 1.3, color: t.secondary }
      : { fontSize: 14, lineHeight: 1.35, color: t.secondary };
   const price = t.classic ? { fontFamily: t.display, fontWeight: 600, fontSize: 19, lineHeight: 1 } : { fontSize: 16 };

   const card = t.classic
      ? { background: "transparent", borderBottom: row ? `1px solid ${t.hair}` : "none" }
      : { background: t.panel, borderRadius: t.r.card, boxShadow: `0 1px 2px rgba(15,23,42,0.05), ${t.ring}` };

   return (
      <div className={`flex h-full w-full overflow-hidden ${row ? "flex-row-reverse" : "flex-col"}`} style={card}>
         {row ? (
            <Media t={t} d={d} className="m-3 aspect-square w-[104px] shrink-0 self-start" style={{ borderRadius: t.r.inner }} />
         ) : (
            <Media t={t} d={d} className="aspect-[4/3] w-full" />
         )}
         <div
            className="flex min-w-0 flex-1 flex-col gap-1"
            style={{ padding: t.classic ? (row ? "14px 4px 14px 14px" : "16px 2px 6px") : row ? "16px 4px 16px 16px" : 16 }}
         >
            <p style={title}>{d.name}</p>
            <p className="line-clamp-2" style={desc}>
               {d.desc}
            </p>
            <div className="mt-auto flex items-center justify-between gap-3 pt-3">
               <span className="tabular-nums" style={price}>
                  {euro(d.price)}
               </span>
               <PlusButton t={t} />
            </div>
         </div>
      </div>
   );
}

/* Zwevende collectiebalk */
function CollectionBar({ t, active = "hoofd", compact = false }) {
   if (t.classic) {
      return (
         <div className="flex justify-center gap-1 py-1.5" style={{ background: t.page, borderTop: `1px solid ${t.line}`, borderBottom: `1px solid ${t.line}` }}>
            {CATEGORIES.map((c) => (
               <span
                  key={c.id}
                  className="relative whitespace-nowrap px-3.5 py-2"
                  style={{ ...label(t), fontSize: compact ? 10.5 : 12, letterSpacing: "0.16em", color: c.id === active ? t.brand : t.secondary }}
               >
                  {c.name}
                  {c.id === active && <span className="absolute inset-x-3.5 bottom-[5px] h-px" style={{ background: t.brand }} />}
               </span>
            ))}
         </div>
      );
   }
   return (
      <div
         className="flex gap-1 p-1.5"
         style={{
            borderRadius: t.r.panel,
            background: t.dark ? "rgba(28,28,30,0.85)" : "rgba(255,255,255,0.8)",
            boxShadow: `0 8px 24px -16px rgba(15,23,42,0.3), ${t.ring}`,
         }}
      >
         {CATEGORIES.map((c) => {
            const Icon = CAT_ICONS[c.id];
            const on = c.id === active;
            return (
               <span
                  key={c.id}
                  className="flex items-center gap-2 whitespace-nowrap px-3.5 py-2 font-medium"
                  style={{ borderRadius: 999, fontSize: compact ? 13 : 14, background: on ? t.accent : "transparent", color: on ? t.accentText : t.text }}
               >
                  <Icon size={compact ? 15 : 17} strokeWidth={2} />
                  {c.name}
               </span>
            );
         })}
      </div>
   );
}

/* Collectie: kop met icoon (modern) of titel in de bovenrand van het kader (klassiek) */
function Collection({ t, cat, children, padding }) {
   const Icon = CAT_ICONS[cat.id];
   if (t.classic) {
      return (
         <section className="relative" style={{ ...frame(t), marginTop: 14, padding: padding?.classic || "44px 26px 26px" }}>
            <p
               className="absolute left-1/2 top-0 whitespace-nowrap text-center"
               style={{
                  ...heading(t, padding?.title || 28),
                  letterSpacing: "0.18em",
                  background: t.panel,
                  padding: "0 0.62em 0 0.8em",
                  transform: "translate(-50%, calc(-50% + 3px))",
               }}
            >
               {cat.name}
            </p>
            {children}
         </section>
      );
   }
   return (
      <section style={{ ...frame(t), padding: padding?.modern || 20 }}>
         <div className="mb-4 flex items-center gap-3 px-1">
            <span className="flex h-9 w-9 items-center justify-center" style={{ borderRadius: 12, background: t.tertiary }}>
               <Icon size={19} strokeWidth={2} />
            </span>
            <p style={heading(t, 24)}>{cat.name}</p>
         </div>
         {children}
      </section>
   );
}

function Logo({ t, className }) {
   return <img src={t.dark ? logoDark : logoLight} alt="" loading="lazy" className={`!h-auto ${className}`} />;
}

/* ---------------- zijbalk en winkelmandje ---------------- */

function Sidebar({ t }) {
   const rows = [
      { icon: Info, l: "Informatie" },
      { icon: Phone, l: "Contact" },
      { icon: Store, l: "Openingsuren" },
      { icon: Gift, l: "Cadeaubon" },
   ];
   return (
      <aside style={{ ...frame(t), padding: t.classic ? 8 : 0 }}>
         <div className="flex flex-col gap-2.5" style={{ padding: t.classic ? 12 : 10 }}>
            <img src={banner} alt="" loading="lazy" decoding="async" className="aspect-[4/3] !w-full object-cover" style={{ borderRadius: t.r.inner }} />
            {rows.map(({ icon: Icon, l }) => (
               <div
                  key={l}
                  className="flex items-center gap-2.5 px-3.5 py-3"
                  style={{ border: `1px solid ${t.classic ? t.line : t.line}`, borderRadius: t.classic ? 0 : t.r.card }}
               >
                  <Icon size={18} strokeWidth={2} className="shrink-0" />
                  <span className="flex-1" style={label(t)}>
                     {l}
                  </span>
                  <ChevronDown size={18} className="shrink-0" />
               </div>
            ))}
            <span
               className="flex h-10 items-center justify-center gap-1.5 font-semibold"
               style={
                  t.classic
                     ? { border: `1px solid ${t.brand}`, color: t.brand, ...label(t), fontSize: 11, letterSpacing: "0.18em" }
                     : { background: t.accent, color: t.accentText, borderRadius: t.r.control, fontSize: 14 }
               }
            >
               Website <ExternalLink size={13} />
            </span>
         </div>
      </aside>
   );
}

export const MINI_CART = [
   { id: "kroket", qty: 1 },
   { id: "stoof", qty: 1 },
   { id: "mousse", qty: 2 },
];
const cartTotal = MINI_CART.reduce((s, c) => s + dish(c.id).price * c.qty, 0);
const cartCount = MINI_CART.reduce((s, c) => s + c.qty, 0);

function CartPanel({ t, moment }) {
   return (
      <aside style={{ ...frame(t), padding: t.classic ? 8 : 0 }}>
         <div className="flex flex-col gap-4" style={{ padding: t.classic ? 14 : 20 }}>
            <div className="flex items-baseline justify-between">
               <p style={heading(t, t.classic ? 20 : 22)}>Winkelmandje</p>
               <span style={{ fontSize: 13, color: t.secondary }}>{cartCount} items</span>
            </div>
            <ul>
               {MINI_CART.map(({ id, qty }) => {
                  const d = dish(id);
                  return (
                     <li key={id} className="flex gap-3 py-3" style={{ borderBottom: `1px solid ${t.hair}` }}>
                        <img src={d.thumb} alt="" loading="lazy" decoding="async" className="!h-14 !w-14 shrink-0 object-cover" style={{ borderRadius: t.r.thumb }} />
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                           <div className="flex items-start justify-between gap-2">
                              <p
                                 className="leading-snug"
                                 style={t.classic ? { fontFamily: t.display, fontWeight: 600, fontSize: 17 } : { fontWeight: 600, fontSize: 15 }}
                              >
                                 {d.name}
                              </p>
                              <p className="shrink-0 tabular-nums" style={{ fontSize: 15 }}>
                                 {euro(d.price * qty)}
                              </p>
                           </div>
                           <div className="mt-1 flex justify-end">
                              <span className="flex items-center p-0.5" style={{ borderRadius: t.r.pill, background: t.tertiary }}>
                                 <span className="flex h-7 w-7 items-center justify-center">
                                    <Minus size={14} />
                                 </span>
                                 <span className="min-w-[20px] text-center font-semibold tabular-nums" style={{ fontSize: 14 }}>
                                    {qty}
                                 </span>
                                 <span className="flex h-7 w-7 items-center justify-center">
                                    <Plus size={14} />
                                 </span>
                              </span>
                           </div>
                        </div>
                     </li>
                  );
               })}
            </ul>
            <div className="flex items-center justify-between px-3.5 py-3" style={{ borderRadius: t.r.inner, background: t.tertiary, fontSize: 13 }}>
               <span style={{ color: t.secondary }}>Afhalen</span>
               <span className="font-semibold">{moment}</span>
            </div>
            <span
               className="flex h-12 items-center justify-center gap-2 font-semibold"
               style={{
                  background: t.accent,
                  color: t.accentText,
                  borderRadius: t.r.control,
                  ...(t.classic ? { fontFamily: t.sans, fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.18em" } : { fontSize: 15 }),
               }}
            >
               Bestellen · <span className="tabular-nums">{euro(cartTotal)}</span>
            </span>
         </div>
      </aside>
   );
}

/* ---------------- de twee layouts ---------------- */

export const DESKTOP = { width: 1200, height: 780 };
export const PHONE = { width: 390, height: 800 };

const byCat = (cat) => DISHES.filter((d) => d.cat === cat);

function DesktopShop({ t, moment }) {
   const main = byCat("hoofd").slice(0, 3);
   return (
      <div className="flex h-full flex-col gap-3 overflow-hidden p-3" style={{ background: t.page, color: t.text, fontFamily: t.sans }}>
         <header
            className="grid shrink-0 grid-cols-[110px_minmax(0,1fr)_110px] items-center gap-4"
            style={{ ...frame(t), padding: t.classic ? "22px 36px" : "16px 32px" }}
         >
            <Logo t={t} className="!w-[110px]" />
            <p
               className="truncate text-center"
               style={t.classic ? { ...heading(t, 32), color: t.brand, letterSpacing: "0.2em" } : heading(t, 28)}
            >
               {SHOP.name}
            </p>
            <span />
         </header>

         <div className="grid min-h-0 flex-1 grid-cols-[210px_minmax(0,1fr)_290px] items-start gap-3">
            <Sidebar t={t} />
            <main className="flex min-w-0 flex-col gap-3">
               <CollectionBar t={t} />
               <Collection t={t} cat={CATEGORIES[1]}>
                  <div className="grid grid-cols-3 gap-3">
                     {main.map((d) => (
                        <ItemCard key={d.id} t={t} d={d} />
                     ))}
                  </div>
               </Collection>
               <Collection t={t} cat={CATEGORIES[2]}>
                  <div className="grid grid-cols-3 gap-3">
                     <ItemCard t={t} d={dish("mousse")} />
                  </div>
               </Collection>
            </main>
            <CartPanel t={t} moment={moment} />
         </div>
      </div>
   );
}

function PhoneShop({ t }) {
   return (
      <div className="relative flex h-full flex-col gap-3 overflow-hidden p-3" style={{ background: t.page, color: t.text, fontFamily: t.sans }}>
         <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_60px] items-center gap-3" style={{ ...frame(t), padding: t.classic ? "18px 22px" : "12px 20px" }}>
            <p style={t.classic ? { ...heading(t, 21), color: t.brand, letterSpacing: "0.12em" } : heading(t, 22)}>{SHOP.name}</p>
            <Logo t={t} className="!w-[60px]" />
         </header>
         <div className="shrink-0 overflow-hidden">
            <CollectionBar t={t} compact />
         </div>
         <Collection t={t} cat={CATEGORIES[1]} padding={{ modern: 12, classic: "34px 14px 10px", title: 22 }}>
            <div className="flex flex-col gap-3">
               {byCat("hoofd").map((d) => (
                  <ItemCard key={d.id} t={t} d={d} row />
               ))}
            </div>
         </Collection>

         {/* zwevende winkelmandknop */}
         <div className="absolute inset-x-3 bottom-4">
            <span
               className="flex h-14 w-full items-center gap-3 pl-2 pr-5 font-semibold shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)]"
               style={{
                  borderRadius: t.r.pill,
                  background: t.accent,
                  color: t.accentText,
                  ...(t.classic ? { fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.1em" } : { fontSize: 16 }),
               }}
            >
               <span className="relative flex h-10 w-10 items-center justify-center" style={{ borderRadius: t.r.pill, background: "rgba(255,255,255,0.15)" }}>
                  <ShoppingBag size={19} />
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-bold text-black">
                     {cartCount}
                  </span>
               </span>
               <span className="flex-1 text-left">Bekijk winkelmandje</span>
               <span className="tabular-nums">{euro(cartTotal)}</span>
            </span>
         </div>
      </div>
   );
}

export default function ShopMini({ theme = "modern", dark = false, brand, layout = "desktop", moment = "Zaterdag 18:30" }) {
   const t = shopTokens(theme, dark, brand);
   return layout === "phone" ? <PhoneShop t={t} /> : <DesktopShop t={t} moment={moment} />;
}

/* Rendert iets op vaste ontwerpmaat en schaalt het naar de beschikbare breedte */
export function ScaledStage({ pick, className = "", style, children }) {
   const ref = useRef(null);
   const [w, setW] = useState(0);
   useLayoutEffect(() => {
      const el = ref.current;
      if (!el) return;
      setW(el.clientWidth);
      const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
      ro.observe(el);
      return () => ro.disconnect();
   }, []);
   const cfg = pick(w);
   const s = w ? w / cfg.width : 0;
   return (
      <div ref={ref} className={`relative w-full overflow-hidden ${className}`} style={{ height: cfg.height * s, ...style }}>
         {s > 0 && (
            <div className="absolute left-0 top-0" style={{ width: cfg.width, height: cfg.height, transform: `scale(${s})`, transformOrigin: "0 0" }}>
               {children(cfg)}
            </div>
         )}
      </div>
   );
}
